import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { supabase } from '../lib/supabaseClient.js'

const ROLE_CACHE = new Map()

const AuthContext = createContext(null)

const initialState = {
  session: null,
  profile: null,
  status: 'loading',
  error: null,
  userRole: null,
}

export function AuthProvider({ children }) {
  const [state, setState] = useState(initialState)
  const isSupabaseConfigured = Boolean(supabase)

  const fetchUserRole = async (userId) => {
    if (!supabase) return null
    if (ROLE_CACHE.has(userId)) {
      const role = ROLE_CACHE.get(userId)
      setState((prev) => ({ ...prev, userRole: role }))
      return role
    }
    try {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .single()
      const role = data?.role ?? null
      ROLE_CACHE.set(userId, role)
      setState((prev) => ({ ...prev, userRole: role }))
      return role
    } catch {
      setState((prev) => ({ ...prev, userRole: null }))
      return null
    }
  }

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setState((prev) => ({ ...prev, status: 'ready' }))
      return
    }

    let isMounted = true

    const loadSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession()

        if (!isMounted) return

        setState((prev) => ({
          ...prev,
          session: session ?? null,
          status: 'ready',
          error,
        }))

        if (session) {
          await hydrateProfile(session.user)
          fetchUserRole(session.user.id)
        }
      } catch (error) {
        if (!isMounted) return
        console.error('[AuthProvider] Failed to establish session', error)
        setState((prev) => ({ ...prev, session: null, profile: null, status: 'error', error }))
      }
    }

    const hydrateProfile = async (user) => {
      try {
        const { data, error } = await supabase.rpc('get_current_profile')
        if (!isMounted) return

        if (error) {
          console.error('[AuthProvider] Failed to load profile', error)
        }

        setState((prev) => ({
          ...prev,
          profile: data ?? null,
        }))
      } catch (error) {
        if (!isMounted) return
        console.error('[AuthProvider] Unexpected profile load error', error)
      }
    }

    loadSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setState((prev) => ({
        ...prev,
        session: session ?? null,
        status: 'ready',
      }))

      if (session) {
        hydrateProfile(session.user)
        fetchUserRole(session.user.id)
      } else {
        ROLE_CACHE.clear()
        setState((prev) => ({ ...prev, profile: null, userRole: null }))
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [isSupabaseConfigured])

  const ensureSupabase = () => {
    if (!isSupabaseConfigured) {
      throw new Error('Authentication service is temporarily unavailable.')
    }
  }

  const signInWithPassword = async ({ email, password }) => {
    ensureSupabase()
    const response = await supabase.auth.signInWithPassword({ email, password })

    if (response.error) {
      throw response.error
    }

    return response.data.session
  }

  const callAuthEmailFunction = async (payload) => {
    ensureSupabase()
    const response = await fetch('/.netlify/functions/auth-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    let result = null
    try {
      result = await response.json()
    } catch (error) {
      result = null
    }

    if (!response.ok) {
      const message = result?.message ?? 'Failed to process auth email request.'
      const error = new Error(message)
      error.status = response.status
      throw error
    }

    return result ?? { ok: true }
  }

  const signUpWithPassword = async ({ email, password, options }) => {
    ensureSupabase()
    if (!email || !password) {
      throw new Error('Email and password are required to request registration.')
    }

    await callAuthEmailFunction({
      action: 'signup',
      email,
      password,
      redirectTo: options?.emailRedirectTo,
    })

    return { email }
  }

  const sendInviteEmail = async ({ email, redirectTo } = {}) => {
    ensureSupabase()
    if (!email) {
      throw new Error('Email is required to send an invite.')
    }

    await callAuthEmailFunction({
      action: 'invite',
      email,
      redirectTo,
    })
  }

  const signOut = async () => {
    ensureSupabase()
    const { error } = await supabase.auth.signOut()

    if (error) {
      throw error
    }
  }

  const sendPasswordReset = async (email, { redirectTo } = {}) => {
    ensureSupabase()
    if (!email) {
      throw new Error('Email is required to request a password reset.')
    }

    await callAuthEmailFunction({
      action: 'recovery',
      email,
      redirectTo,
    })
  }

  const requestEmailChange = async (newEmail) => {
    ensureSupabase()
    if (!state.session?.user?.email) {
      throw new Error('You must be signed in to request an email change.')
    }

    const trimmed = newEmail?.trim()
    if (!trimmed) {
      throw new Error('Enter the new email address you would like to use.')
    }

    const currentEmail = state.session.user.email

    if (trimmed.toLowerCase() === currentEmail.toLowerCase()) {
      throw new Error('Use a different email than your current one.')
    }

    await callAuthEmailFunction({
      action: 'email_change',
      email: currentEmail,
      newEmail: trimmed,
    })
  }

  const refreshProfile = async () => {
    if (!isSupabaseConfigured || !state.session) return null
    const { data, error } = await supabase.rpc('get_current_profile')
    if (error) {
      console.error('[AuthProvider] Failed to refresh profile', error)
      return null
    }
    setState((prev) => ({ ...prev, profile: data ?? null }))
    return data ?? null
  }

  const signIn = async (email, password) => {
    try {
      ensureSupabase()
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return { error: error ?? null }
    } catch (err) {
      return { error: err }
    }
  }

  const value = useMemo(
    () => ({
      session: state.session,
      user: state.session?.user ?? null,
      profile: state.profile,
      status: state.status,
      error: state.error,
      isAuthenticated: Boolean(state.session?.user),
      isLoading: state.status === 'loading',
      userRole: state.userRole,
      isAdmin: state.userRole === 'admin',
      isStaff: state.userRole === 'staff' || state.userRole === 'admin',
      signIn,
      signInWithPassword,
      signUpWithPassword,
      sendInviteEmail,
      signOut,
      sendPasswordReset,
      requestEmailChange,
      refreshProfile,
    }),
    [state.session, state.profile, state.status, state.error, state.userRole],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
